import React, { useState, useEffect, useMemo } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { format, addHours, addDays } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { ptBR } from 'date-fns/locale';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import {
  Loader2, Copy, Check, AlertTriangle, Video,
  CheckCircle2, X, Plus as PlusIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useCalendarEventMutations } from '@/hooks/useCalendarEvents';
import { useAgendaEvents } from '@/hooks/useAgendaEvents';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';
import { useAppUsers } from '@/hooks/useAppUsers';

const DURATIONS = [
  { label: '30 minutos', value: 30 },
  { label: '1 hora', value: 60 },
  { label: '1h 30min', value: 90 },
  { label: '2 horas', value: 120 },
];

function getUserDisplayName(u: {
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
}) {
  return u.display_name || [u.first_name, u.last_name].filter(Boolean).join(' ') || '—';
}

interface ScheduleMeetingModalProps {
  open: boolean;
  onClose: () => void;
  dealId: string;
  contactId: string;
  dealName?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  /** Pre-fill the host dropdown with the deal's consultant */
  defaultConsultantId?: string | null;
}

export function ScheduleMeetingModal({
  open,
  onClose,
  dealId,
  contactId,
  dealName,
  contactName,
  contactEmail,
  defaultConsultantId,
}: ScheduleMeetingModalProps) {
  const { data: currentUser } = useCurrentAppUser();
  const { createEvent } = useCalendarEventMutations();
  const { users: allUsers } = useAppUsers();
  const { userTimezone } = useUserTimezone();

  // ── Form state ─────────────────────────────────────────────────────────────
  const tomorrow = addDays(new Date(), 1);
  const [hostId, setHostId] = useState<string>(defaultConsultantId ?? '');
  const [title, setTitle] = useState('');
  const [selectedDate, setSelectedDate] = useState(safeFormat(tomorrow, 'yyyy-MM-dd'));
  const [startTime, setStartTime] = useState('09:00');
  const [duration, setDuration] = useState(60);
  const [description, setDescription] = useState('');

  // ── Result state (after creation) ─────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{
    roomUrl: string;
    clientJoinUrl: string;
    scheduledAt: Date;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  // ── Reset on open ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const d = addDays(new Date(), 1);
    setHostId(defaultConsultantId ?? '');
    setTitle(dealName ? `Reunião – ${dealName}` : 'Reunião com Cliente');
    setSelectedDate(safeFormat(d, 'yyyy-MM-dd'));
    setStartTime('09:00');
    setDuration(60);
    setDescription('');
    setResult(null);
    setCopied(false);
  }, [open, dealName, contactEmail, defaultConsultantId]);

  // ── Slot interpreted in the VIEWER's timezone, then converted to UTC ───────
  // The user picks "14:00 on 2026-04-17" in their own (browser/profile) timezone.
  // We convert that wall-clock time to a UTC Date for storage/comparison.
  const { start: slotStart, end: slotEnd } = useMemo(() => {
    const wallClock = `${selectedDate}T${startTime}:00`;
    const start = fromZonedTime(wallClock, userTimezone);
    const end = addHours(start, duration / 60);
    return { start, end };
  }, [selectedDate, startTime, duration, userTimezone]);

  // Query month based on the selected date in viewer's tz
  const queryYear = useMemo(
    () => Number(formatInTimeZone(slotStart, userTimezone, 'yyyy')),
    [slotStart, userTimezone],
  );
  const queryMonth = useMemo(
    () => Number(formatInTimeZone(slotStart, userTimezone, 'M')),
    [slotStart, userTimezone],
  );

  const { data: hostMonthEvents = [], isLoading: loadingConflicts } = useAgendaEvents(
    queryYear,
    queryMonth,
    hostId || null,
  );

  // Day filter: compare YYYY-MM-DD strings in VIEWER's timezone
  const selectedDayKey = useMemo(
    () => formatInTimeZone(slotStart, userTimezone, 'yyyy-MM-dd'),
    [slotStart, userTimezone],
  );

  const dayEvents = useMemo(
    () =>
      hostMonthEvents.filter(
        (ev) =>
          formatInTimeZone(new Date(ev.start_at), userTimezone, 'yyyy-MM-dd') === selectedDayKey,
      ),
    [hostMonthEvents, selectedDayKey, userTimezone],
  );

  // Conflict comparison: absolute UTC timestamps (overlap test)
  // Skip all-day items (tasks without time, full-day events) — they don't block a time slot
  const conflictingEvents = useMemo(
    () =>
      dayEvents.filter((ev) => {
        if (ev.all_day) return false;
        const evStart = new Date(ev.start_at);
        const evEnd = new Date(ev.end_at);
        if (evEnd.getTime() <= evStart.getTime()) return false;
        return slotStart < evEnd && slotEnd > evStart;
      }),
    [dayEvents, slotStart, slotEnd],
  );

  const hasConflict = conflictingEvents.length > 0;
  const isAvailable = !!hostId && !loadingConflicts && !hasConflict;

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !hostId) return;
    setSubmitting(true);
    try {
      // 1. Create Daily.co room (scheduled type)
      const { data: roomData, error: roomError } = await supabase.functions.invoke(
        'create-daily-room',
        {
          body: {
            deal_id: dealId,
            contact_id: contactId,
            consultant_id: hostId,
            type: 'scheduled',
            end_at: slotEnd.toISOString(),
          },
        },
      );

      if (roomError || !roomData?.room_url) {
        throw new Error(roomError?.message ?? 'Erro ao criar sala Daily.co');
      }

      // 2. Create calendar_events entry
      //    created_by = SDR (current user); attendee_ids includes the consultant (host)
      //    so the consultant sees this in their own calendar
      const attendeeIds: string[] = [];
      if (hostId !== currentUser.id) attendeeIds.push(hostId);

      await createEvent.mutateAsync({
        title: title.trim() || 'Reunião com Cliente',
        type: 'meeting',
        status: 'scheduled',
        all_day: false,
        start_at: slotStart.toISOString(),
        end_at: slotEnd.toISOString(),
        description: description.trim() || null,
        created_by: currentUser.id,
        attendee_ids: attendeeIds,
        contact_id: contactId,
        deal_id: dealId,
        daily_room_url: roomData.room_url,
        daily_room_name: roomData.room_name ?? null,
        color: null,
      });

      // 3. Mirror into `calls` so the meeting appears in deal/contact call history
      //    and the Daily.co webhook can track status + recording by meeting_url.
      //    Errors here are non-fatal — calendar_event is the source of truth.
      await supabase.from('calls').insert({
        contact_id: contactId,
        deal_id: dealId,
        channel: 'video',
        status: 'scheduled',
        scheduled_for: slotStart.toISOString(),
        meeting_url: roomData.room_url,
        title: title.trim() || 'Reunião com Cliente',
        call_purpose: 'scheduled_video',
        handled_by_app_user_id: hostId,
        created_by: currentUser.id,
        direction: 'outbound',
      }).then(({ error }) => {
        if (error) console.warn('[ScheduleMeetingModal] calls mirror failed:', error.message);
      });

      setResult({
        roomUrl: roomData.room_url,
        clientJoinUrl: roomData.client_join_url ?? roomData.room_url,
        scheduledAt: slotStart,
      });
    } catch (err: any) {
      toast.error(err?.message ?? 'Erro ao agendar reunião');
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(result.clientJoinUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
    toast.success('Link copiado!');
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="w-4 h-4" />
            Agendar Videochamada
          </DialogTitle>
        </DialogHeader>

        {/* ── Success ── */}
        {result ? (
          <div className="space-y-4 pt-2">
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-1 text-center">
              <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-1" />
              <p className="font-semibold text-green-800">Reunião agendada!</p>
              <p className="text-sm text-green-700">
                {formatInTimeZone(result.scheduledAt, userTimezone, "EEEE, d 'de' MMMM 'às' HH:mm", { locale: ptBR })
                  .replace(/^\w/, (c) => c.toUpperCase())}
              </p>
            </div>

            <div className="space-y-1">
              <Label>Link do cliente</Label>
              <div className="flex gap-2">
                <Input value={result.clientJoinUrl} readOnly className="text-xs" />
                <Button type="button" size="icon" variant="outline" onClick={copyLink}>
                  {copied ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Envie este link para o cliente entrar na videochamada
              </p>
            </div>

            <Button className="w-full" onClick={onClose}>
              Fechar
            </Button>
          </div>
        ) : (
          /* ── Form ── */
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            {/* Call type (fixed) */}
            <div className="space-y-1">
              <Label>Tipo de Chamada</Label>
              <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-input bg-muted/40 text-sm text-muted-foreground">
                <Video className="w-4 h-4" />
                Chamada de Vídeo (Daily.co)
              </div>
            </div>

            {/* Title */}
            <div className="space-y-1">
              <Label htmlFor="sm-title">Título da Reunião *</Label>
              <Input
                id="sm-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex: Consulta inicial"
                required
              />
            </div>

            {/* Host (Consultor) */}
            <div className="space-y-1">
              <Label>Anfitrião *</Label>
              <Select value={hostId} onValueChange={setHostId} required>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o consultor" />
                </SelectTrigger>
                <SelectContent>
                  {allUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {getUserDisplayName(u)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date + Time */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="sm-date">Data *</Label>
                <Input
                  id="sm-date"
                  type="date"
                  value={selectedDate}
                  min={safeFormat(new Date(), 'yyyy-MM-dd')}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="sm-time">Horário *</Label>
                <Input
                  id="sm-time"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Duration */}
            <div className="space-y-1">
              <Label>Duração</Label>
              <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATIONS.map((d) => (
                    <SelectItem key={d.value} value={String(d.value)}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Availability / Conflict check */}
            {hostId && (
              <div className="space-y-2">
                <Label>Disponibilidade do anfitrião</Label>
                {loadingConflicts ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Verificando agenda…
                  </div>
                ) : hasConflict ? (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-amber-800">
                      <AlertTriangle className="w-4 h-4 shrink-0" />
                      Conflito de horário
                    </div>
                    {conflictingEvents.map((ev) => (
                      <div key={ev.id} className="text-xs text-amber-700 pl-6">
                        {formatInTimeZone(new Date(ev.start_at), userTimezone, 'HH:mm')}–
                        {formatInTimeZone(new Date(ev.end_at), userTimezone, 'HH:mm')} · {ev.title}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-green-700">
                    <CheckCircle2 className="w-4 h-4" />
                    Horário disponível
                  </div>
                )}

                {/* Other events on this day (non-conflicting) */}
                {dayEvents.length > 0 && !loadingConflicts && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">
                      Outros eventos neste dia ({formatInTimeZone(slotStart, userTimezone, 'd MMM', { locale: ptBR })}) · fuso {userTimezone}:
                    </p>
                    <div className="space-y-1 max-h-24 overflow-y-auto">
                      {dayEvents.map((ev) => (
                        <div
                          key={ev.id}
                          className={cn(
                            'flex items-center gap-2 text-xs rounded px-2 py-1',
                            conflictingEvents.some((c) => c.id === ev.id)
                              ? 'bg-amber-100'
                              : 'bg-muted',
                          )}
                        >
                          <span className="text-muted-foreground w-11 shrink-0">
                            {formatInTimeZone(new Date(ev.start_at), userTimezone, 'HH:mm')}
                          </span>
                          <span className="truncate">{ev.title}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            <div className="space-y-1">
              <Label htmlFor="sm-desc">Descrição</Label>
              <Textarea
                id="sm-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Detalhes adicionais da reunião…"
                rows={3}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting || !hostId || !title.trim()}>
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Agendar reunião
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
